import api from '../../../utils/functions/api';

const BASE = process.env.REACT_APP_ACCOUNTING_API_URL;

// helper: ดึง error message ที่มีความหมายจาก axios error
function extractMessage(error) {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ'
  );
}

export const listBankAccounts = () => async () => {
  try {
    const { data } = await api.get(`${BASE}/bank-accounts`);
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// Sync Postgres ของ accounting-service เข้า dev DB — คู่กับ /sync-dev-db ของ backend/
// (Mongo) ปุ่ม "เริ่ม Sync" ใน Developer.js ยิงทั้งสองตัวพร้อมกัน
export const syncAccountingDevDb = () => async () => {
  try {
    const { data } = await api.post(`${BASE}/sync-dev-db`);
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const createBankAccount = ({
  name, bank, accountNumber, customerId, customerIdRepay, bankRepay, accountNumberRepay, mode,
}) => async () => {
  try {
    const { data } = await api.post(`${BASE}/bank-accounts`, {
      name,
      bank,
      account_number: accountNumber,
      customer_id: customerId,
      customer_id_repay: customerIdRepay,
      bank_repay: bankRepay,
      account_number_repay: accountNumberRepay,
      mode,
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const updateBankAccount = ({
  id, name, bank, accountNumber, customerId, customerIdRepay, bankRepay, accountNumberRepay,
  ocrEnabled, openingBalance, ocrOpeningBalance, companyOpeningBalance, companyOpeningBalanceNote,
}) => async () => {
  try {
    const { data } = await api.patch(`${BASE}/bank-accounts/${id}`, {
      name,
      bank,
      account_number: accountNumber,
      customer_id: customerId,
      customer_id_repay: customerIdRepay,
      bank_repay: bankRepay,
      account_number_repay: accountNumberRepay,
      ...(ocrEnabled !== undefined ? { ocr_enabled: ocrEnabled } : {}),
      ...(openingBalance !== undefined ? { opening_balance: openingBalance } : {}),
      ...(ocrOpeningBalance !== undefined ? { ocr_opening_balance: ocrOpeningBalance } : {}),
      ...(companyOpeningBalance !== undefined ? { company_opening_balance: companyOpeningBalance } : {}),
      ...(companyOpeningBalanceNote !== undefined ? { company_opening_balance_note: companyOpeningBalanceNote } : {}),
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const getReconciliationSummary = ({ bankAccountId, dateFrom, dateTo }) => async () => {
  try {
    const { data } = await api.get(`${BASE}/reconciliation/summary`, {
      params: { bank_account_id: bankAccountId, date_from: dateFrom, date_to: dateTo },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// เรียก backend เดิมตรงๆ (ไม่ผ่าน accounting-service) เพราะเป็น Payment/Expense
// ล้วนๆ ไม่เกี่ยวกับข้อมูล Postgres/OCR ของ accounting-service เลย
export const listPaymentsByCustomer = ({
  customerId, page, size, projectName, projectNumber, dateFrom,
}) => async () => {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_API_URL}/accounting/payments-by-customer`, {
      params: {
        customer_id: customerId,
        page,
        size,
        project_name: projectName,
        project_number: projectNumber,
        date_from: dateFrom,
      },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const listLoanProjectExpenses = ({ customerId, projectName, projectNumber }) => async () => {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_API_URL}/accounting/loan-project-expenses`, {
      params: { customer_id: customerId, project_name: projectName, project_number: projectNumber },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const listCustomerCashFlow = ({
  customerId, direction, page, size, dateFrom, onlyUnverified,
}) => async () => {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow`, {
      params: {
        customer_id: customerId,
        direction,
        page,
        size,
        date_from: dateFrom,
        only_unverified: onlyUnverified || undefined,
      },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const listCustomerCashFlowLedger = ({
  customerId, page, size, dateFrom, boundaries,
}) => async () => {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/ledger`, {
      params: {
        customer_id: customerId,
        page,
        size,
        date_from: dateFrom,
        ...(boundaries && boundaries.length ? { boundaries: boundaries.join(',') } : {}),
      },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const listUnverifiedExpenseDates = ({ customerId, dateFrom }) => async () => {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/unverified-dates`, {
      params: { customer_id: customerId, date_from: dateFrom },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const listCustomerExpensesByDate = ({ customerId, date }) => async () => {
  try {
    const { data } = await api.get(`${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/by-date`, {
      params: { customer_id: customerId, date },
    });
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const extractCashFlowOcr = ({ files }) => async () => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/ocr-extract`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const matchCashFlowOcr = ({
  customerId, dateFrom, dateTo, ocrDateStart, ocrDateEnd, transactions,
}) => async () => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/ocr-match`,
      {
        customer_id: customerId,
        date_from: dateFrom,
        date_to: dateTo,
        ocr_date_start: ocrDateStart,
        ocr_date_end: ocrDateEnd,
        transactions,
      },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const confirmCashFlowOcr = ({
  customerId, imageIds, account, transactions, results, unmatchedTransactions, paymentIds,
}) => async () => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/ocr-confirm`,
      {
        customer_id: customerId,
        image_ids: imageIds,
        account,
        transactions,
        results,
        unmatched_transactions: unmatchedTransactions,
        payment_ids: paymentIds,
      },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const listOcrVerificationHistory = ({ customerId, page, size }) => async () => {
  try {
    const { data } = await api.get(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/ocr-history`,
      { params: { customer_id: customerId, page, size } },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const getOcrVerificationImagesForDate = ({ customerId, date }) => async () => {
  try {
    const { data } = await api.get(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/ocr-images-by-date`,
      { params: { customer_id: customerId, date } },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const extractLoanSlips = ({ files }) => async () => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/accounting/loan-project-expenses/ocr-extract`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const matchLoanSlips = ({
  customerId, projectName, projectNumber, dateFrom, slips,
}) => async () => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/accounting/loan-project-expenses/ocr-match`,
      {
        customer_id: customerId,
        project_name: projectName,
        project_number: projectNumber,
        date_from: dateFrom,
        slips,
      },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const confirmLoanSlipVerification = ({ paymentIds, imageIds }) => async () => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/accounting/loan-project-expenses/ocr-confirm`,
      { payment_ids: paymentIds, image_ids: imageIds },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// รายการที่ไม่มีใน payment ระบบ (Payment เงาจาก OCR) — การ์ดในหน้า company-account
export const listExternalPayments = () => async () => {
  try {
    const { data } = await api.get(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/external-list`,
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const confirmExternalPayment = ({ id, detail }) => async () => {
  try {
    const { data } = await api.patch(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/external/${id}/confirm`,
      { detail },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const deleteExternalPayment = ({ id }) => async () => {
  try {
    const { data } = await api.delete(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/external/${id}`,
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

// payment ในระบบที่ไม่พบในธนาคาร (not_found) — การ์ดในหน้า company-account
export const listNotFoundPayments = ({ customerId, dateFrom }) => async () => {
  try {
    const { data } = await api.get(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/not-found-list`,
      { params: { customer_id: customerId, date_from: dateFrom } },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const manualVerifyPayment = ({ id, note }) => async () => {
  try {
    const { data } = await api.patch(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/payment/${id}/manual-verify`,
      { note },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};

export const markPaymentOutstanding = ({ id, outstanding, note }) => async () => {
  try {
    const { data } = await api.patch(
      `${process.env.REACT_APP_API_URL}/accounting/customer-cash-flow/payment/${id}/mark-outstanding`,
      { outstanding, note },
    );
    return data;
  } catch (error) {
    throw new Error(extractMessage(error));
  }
};
